import { makeAutoObservable, runInAction } from 'mobx';

import { loginSignupController } from "../LoginSignupController";
import { userAccountController } from "../../../../Controllers/UserAccountController";
import { utils } from "../../../../Controllers/Utils";
import { APIUserDTO } from "../../../../DTOs/APIUserDTO";
import { alertController } from "../../../../Controllers/AlertController";
import { countryController } from '../../../../Controllers/CountryController';
import { Zone } from '../../../../DTOs/Address/Country';
import { addressesController } from '../../../../Controllers/AddressesController';
import { StockistCoverAlertFrequency } from '../../../../DTOs/StockistDTO';

export class SignupController {
  step: 1 | 2 = 1
  stockistName = ""
  email = ""
  password = ""
  firstName = ""
  lastName = ""
  companyName = ""
  address1 = ""
  address2 = ""
  address3 = ""
  address4 = ""
  country = ""
  postcode = ""
  contactNumber = ""
  VATNo = ""
  eori = ""
  stockistType = ""
  otherStockistType = ""
  coverAlertFrequency = StockistCoverAlertFrequency.OnceAWeek
  loading = false;

  get currentStockistType() {
    return this.stockistType === "Other" ? this.otherStockistType : this.stockistType;
  }

  get stockistNameInputError() {
    return !!this.stockistName && this.stockistName.length < 2;
  }

  get stockistTypeInputError() {
    return !!this.currentStockistType && (this.currentStockistType.length < 2 || this.currentStockistType.length > 40);
  }

  get emailInputError() {
    return !!this.email.length && !utils.emailCorrect(this.email);
  }

  get passwordInputError() {
    return !!this.password && this.password.length < 8;
  }

  get firstNameInputError() {
    return !!this.firstName && (this.firstName.length < 2 || this.firstName.length > 20);
  }

  get lastNameInputError() {
    return !!this.lastName && (this.lastName.length < 2 || this.lastName.length > 20);
  }

  get companyNameInputError() {
    return !!this.companyName && (this.companyName.length < 2 || this.companyName.length > 50);
  }

  get address1InputError() {
    return !!this.address1 && (this.address1.length < 2 || this.address1.length > 40);
  }

  get address2InputError() {
    return !!this.address2 && this.address2.length > 40;
  }

  get address3InputError() {
    return !!this.address3 && (this.address3.length < 2 || this.address3.length > 40);
  }

  get address4InputError() {
    return !!this.address4 && (this.address4.length < 2 || this.address4.length > 40);
  }

  get countryInputError() {
    return !this.country;
  }

  get postcodeInputError() {
    return !!this.postcode && (this.postcode.length < 2 || this.postcode.length > 30); //&& !utils.postcodeCorrect(this.postcode)
  }

  get contactNumberInputError() {
    return !!this.contactNumber.length && !utils.phoneNumberCorrect(this.contactNumber)
  }

  get VATNoInputError() {
    return !!this.VATNo && this.VATNo.length < 2;
  }

  get coverAlertFrequencySelectError() {
    return !this.coverAlertFrequency;
  }

  get selectedCountry() {
    return countryController.countries.find(c => c.name === this.country || c.iso_a2 === this.country);
  }

  get isEoriRequired() {
    const zone = this.selectedCountry?.zone;
    return zone === Zone.eu || zone === Zone.denmark;
  }

  get eoriNumberInputError() {
    if (!this.isEoriRequired && !this.eori) return false;
    return !!this.eori && (this.eori.length < 2 || this.eori.length > 18);
  }

  setStockistName(value: string) {
    this.stockistName = value
  }

  setEmail(value: string) {
    this.email = value
  }

  setPassword(value: string) {
    this.password = value
  }

  setFirstName(value: string) {
    this.firstName = value
  }

  setLastName(value: string) {
    this.lastName = value
  }

  setCompanyName(value: string) {
    this.companyName = value
  }

  setAddress1(value: string) {
    this.address1 = value
  }

  setAddress2(value: string) {
    this.address2 = value
  }

  setAddress3(value: string) {
    this.address3 = value
  }

  setAddress4(value: string) {
    this.address4 = value
  }

  setCountry(value: string) {
    this.country = value;
  }

  setPostcode(value: string) {
    this.postcode = value
  }

  setContactNumber(value: string) {
    this.contactNumber = value
  }

  setVATNo(value: string) {
    this.VATNo = value
  }

  setEori(value: string) {
    this.eori = value
  }

  setStockistType(value: string) {
    this.stockistType = value;
    if (value !== "Other") {
      this.otherStockistType = "";
    }
  }

  setOtherStockistType(value: string) {
    this.otherStockistType = value;
  }

  setCoverAlertFrequency(value: StockistCoverAlertFrequency) {
    this.coverAlertFrequency = value;
  }

  async validateUser() {
    runInAction(() => this.loading = true)
    try {
      const validation = await userAccountController.validateUser(
        this.stockistName,
        this.email,
        this.password,
        this.firstName,
        this.lastName,
        this.companyName,
        this.address1,
        this.address2,
        this.address3,
        this.address4,
        this.country,
        this.postcode,
        this.contactNumber,
        this.VATNo,
        this.eori,
        this.currentStockistType,
        this.coverAlertFrequency
      );

      runInAction(() => this.loading = false)
      return validation
    } catch (error) {
      alertController.displayAlert("Error while trying to sign up", (error as Error).message, "error")
      runInAction(() => {
        runInAction(() => this.loading = false)
        this.step = 1
      })
    }
  }

  async validateFirstStep() {
    runInAction(() => this.loading = true)
    try {
      const validation = await userAccountController.validateInitialUserData(
        this.stockistName,
        this.currentStockistType,
        this.email,
        this.password,
        this.firstName,
        this.lastName,
        this.VATNo
      );
      runInAction(() => this.loading = false)
      return validation
    } catch (error) {
      alertController.displayAlert("Error validating initial details, please try again.", (error as Error).message, "error")
      runInAction(() => {
        runInAction(() => this.loading = false)
        this.step = 1
      })
    }
  }

  async signUp(): Promise<APIUserDTO | undefined> {
    this.loading = true
    try {
      const validation = await userAccountController.validateUser(
        this.stockistName,
        this.email,
        this.password,
        this.firstName,
        this.lastName,
        this.companyName,
        this.address1,
        this.address2,
        this.address3,
        this.address4,
        this.country,
        this.postcode,
        this.contactNumber,
        this.VATNo,
        this.eori,
        this.currentStockistType,
        this.coverAlertFrequency
      );

      if (!validation.isValid) {
        alertController.displayAlert(
          "Validation failed",
          validation.errors.map(e => e.errorMessage),
          "error",
          4000
        )

        runInAction(() => this.loading = false)
        return
      }

      const apiUser = await userAccountController.signUp(
        this.stockistName,
        this.email,
        this.password,
        this.firstName,
        this.lastName,
        this.companyName,
        this.address1,
        this.address2,
        this.address3,
        this.address4,
        this.country,
        this.postcode,
        this.contactNumber,
        this.VATNo,
        this.eori,
        this.currentStockistType,
        this.coverAlertFrequency
      )
      if (apiUser) {
        loginSignupController.setLoginSignupWindowVisible(false);
        addressesController.fetchAddresses();
      }
      runInAction(() => this.loading = false)
      return apiUser
    } catch (error) {
      alertController.displayAlert("Error while trying to sign up", (error as Error).message, "error")
      runInAction(() => {
        this.loading = false
        this.step = 1
      })
    }
  }

  get step2Disabled() {
    return this.stockistNameInputError || !this.stockistName ||
      this.stockistTypeInputError ||
      this.emailInputError || !this.email ||
      this.passwordInputError || !this.password ||
      this.firstNameInputError || !this.firstName ||
      this.lastNameInputError || !this.lastName ||
      this.VATNoInputError || !this.VATNo
  }

  get signupDisabled() {
    return this.companyNameInputError || !this.companyName ||
      this.address1InputError || !this.address1 ||
      this.address2InputError ||
      this.address3InputError || !this.address3 ||
      (this.country === "United States" ? !this.address4 : this.address4InputError) ||
      this.countryInputError || !this.country ||
      this.postcodeInputError || !this.postcode ||
      this.contactNumberInputError || !this.contactNumber ||
      (this.isEoriRequired && (!this.eori || this.eoriNumberInputError)) ||
      this.coverAlertFrequencySelectError
  }

  nextStep = () => {
    this.step += 1
  }

  previousStep = () => {
    this.step -= 1
  }

  goToLogin = () => {
    loginSignupController.setCurrentWindow("LOGIN")
  }

  constructor() {
    makeAutoObservable(this);
  }
}

export const signupController = new SignupController()
